'use client';
// ═══════════════════════════════════════════════════════════════════════════
// AUREUS FLOW — page publique d'inscription self-serve (27/09/2026)
// ───────────────────────────────────────────────────────────────────────────
// Route Next.js réelle et autonome (≠ les écrans de app/pages/, qui sont des
// composants internes au SPA du dashboard, jamais atteignables sans compte).
// Ici, l'inverse est le but : une société qui n'a AUCUN lien avec Aureus doit
// pouvoir s'inscrire seule, sans repasser par le staff. Elle poste sur
// /api/flow/signup, qui crée le compte (rôle 'flow_client', périmètre étroit
// — voir permissions.js) et démarre l'essai de 14 jours.
//
// 🇳🇱 FR + NL depuis le 28/09/2026 (R8/R8 bis) : le même composant reçoit
// `lang`, la page /nl/flow le rend en néerlandais. Ce bloc disait « FR
// uniquement, dette assumée » — la dette est soldée, la note aussi.
//
// ⏱ Bloc « Pointage obligatoire 2027 » (28/09/2026) : obligation générale
// d'enregistrement du temps de travail annoncée au 01/01/2027 — AVANT-PROJET
// de loi, non publié au Moniteur (UCM, Securex, lus le 28/09/2026 ; sentinelle
// veille-sources-legales.js). Le texte le dit tel quel : on vend ce qui est
// vrai, au cran où c'est vrai (R2 bis).
//
// ⚠️ Prix affichés = ceux de flow-pricing.js, PROVISOIRES (non validés par le
// fondateur au 27/09/2026 — « on reviendra sur les prix plus tard »).
// ═══════════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { FLOW_SOCLE_MENSUEL, FLOW_PRIX_TRAVAILLEUR, FLOW_ESSAI_JOURS } from '@/app/lib/flow-pricing';

const TEAL = '#14b8a6';
const DARK = '#0c0b14';
const BG2 = '#111019';
const BORDER = 'rgba(20,184,166,.18)';
const TEXT = '#e8e6e0';
const MUTED = '#9e9b93';

const SECTEURS = {
  fr: ['Restauration', 'Hôtellerie', 'Retail', 'Santé', 'Industrie', 'Logistique', 'Événementiel', 'Loisirs', 'Service à la personne', 'Autres'],
  nl: ['Horeca — restaurant', 'Hotel', 'Retail', 'Zorg', 'Industrie', 'Logistiek', 'Evenementen', 'Vrije tijd', 'Dienstverlening aan personen', 'Andere'],
};

const T = {
  fr: {
    intro: "Planning, pointeuse GPS et écart Prévu/Réel pour votre équipe — sans devenir client paie d'Aureus.",
    essai: (j) => `Essai ${j} jours, sans carte bancaire.`,
    prix: (socle, trav) => <>À partir de <strong style={{ color: TEXT }}>{socle} €/mois</strong> par société + <strong style={{ color: TEXT }}>{trav} €</strong> par travailleur planifié — pas l'effectif total.</>,
    t2027: 'Pointage obligatoire en 2027',
    p2027: "Le gouvernement a annoncé une obligation générale d'enregistrement du temps de travail au 1er janvier 2027, avec une tolérance jusqu'au 31 mars 2027 (avant-projet de loi, non encore publié au Moniteur). Le système doit être objectif, fiable et accessible au travailleur ; aucune badgeuse n'est imposée. Aureus Flow enregistre les heures de chaque travailleur à l'heure du serveur, et chacun consulte et télécharge son relevé depuis son téléphone.",
    cree: 'Compte créé.', mailParti: 'Un email de confirmation vient de partir.', seConnecter: 'Se connecter →',
    societe: 'Nom de la société', secteur: 'Secteur', optionnel: '— optionnel —', email: 'Email professionnel',
    mdp: 'Mot de passe (8 caractères min.)', creation: 'Création…', demarrer: "Démarrer l'essai gratuit",
    erreurInconnue: 'Erreur inconnue', indisponible: 'Le service est momentanément indisponible. Réessayez.',
    note: "Aureus Flow n'est pas un secrétariat social : aucune fiche de paie ni déclaration ONSS n'est produite pour votre société.",
    placeholderSociete: 'Ex. Belga Queen SPRL', placeholderEmail: 'vous@societe.be',
    dejaCompte: 'Déjà un compte ?', seConnecterSimple: 'Se connecter',
  },
  nl: {
    intro: 'Planning, GPS-prikklok en verschil Gepland/Reëel voor uw team — zonder loonklant van Aureus te worden.',
    essai: (j) => `${j} dagen gratis proberen, zonder bankkaart.`,
    prix: (socle, trav) => <>Vanaf <strong style={{ color: TEXT }}>{socle} €/maand</strong> per vennootschap + <strong style={{ color: TEXT }}>{trav} €</strong> per ingeplande werknemer — niet het volledige personeelsbestand.</>,
    t2027: 'Verplichte tijdsregistratie in 2027',
    p2027: 'De regering kondigde een algemene verplichting tot tijdsregistratie aan vanaf 1 januari 2027, met een tolerantie tot 31 maart 2027 (voorontwerp van wet, nog niet gepubliceerd in het Staatsblad). Het systeem moet objectief, betrouwbaar en toegankelijk zijn voor de werknemer; een prikklok wordt niet opgelegd. Aureus Flow registreert de uren van elke werknemer met de servertijd, en iedereen raadpleegt en downloadt zijn overzicht op zijn telefoon.',
    cree: 'Account aangemaakt.', mailParti: 'Er is zonet een bevestigingsmail vertrokken.', seConnecter: 'Aanmelden →',
    societe: 'Naam van de vennootschap', secteur: 'Sector', optionnel: '— optioneel —', email: 'Professioneel e-mailadres',
    mdp: 'Wachtwoord (min. 8 tekens)', creation: 'Aanmaken…', demarrer: 'Gratis proefperiode starten',
    erreurInconnue: 'Onbekende fout', indisponible: 'De dienst is tijdelijk onbeschikbaar. Probeer opnieuw.',
    note: 'Aureus Flow is geen sociaal secretariaat: er wordt geen loonfiche noch RSZ-aangifte opgemaakt voor uw vennootschap.',
    placeholderSociete: 'Bv. Belga Queen BV', placeholderEmail: 'u@bedrijf.be',
    dejaCompte: 'Al een account?', seConnecterSimple: 'Aanmelden',
  },
};

export default function FlowSignupPage({ lang = 'fr' }) {
  const L = T[lang] || T.fr;
  const secteurs = SECTEURS[lang] || SECTEURS.fr;
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [secteur, setSecteur] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const r = await fetch('/api/flow/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom, email, password, secteur: secteur || undefined }),
      });
      const j = await r.json();
      if (!r.ok) { setResult({ error: j.error || L.erreurInconnue }); return; }
      setResult({ ok: true, loginUrl: j.loginUrl });
    } catch (err) {
      setResult({ error: L.indisponible });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: DARK, color: TEXT, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '48px 20px' }}>
        <h1 style={{ color: TEAL, fontSize: 28, margin: '0 0 8px' }}>Aureus Flow</h1>
        <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.6, margin: '0 0 28px' }}>
          {L.intro} {L.essai(FLOW_ESSAI_JOURS)}
        </p>
        <div style={{ background: BG2, border: `1px solid ${BORDER}`, borderRadius: 12, padding: '14px 18px', marginBottom: 16, fontSize: 13, color: MUTED }}>
          {L.prix(FLOW_SOCLE_MENSUEL, FLOW_PRIX_TRAVAILLEUR)}
        </div>
        <div style={{ background: 'rgba(20,184,166,.06)', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '14px 18px', marginBottom: 24, fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
          <strong style={{ color: TEAL }}>⏱ {L.t2027}</strong><br />
          {L.p2027}
        </div>

        {result?.ok ? (
          <div style={{ background: 'rgba(20,184,166,.08)', border: `1px solid ${TEAL}`, borderRadius: 12, padding: 20, color: TEXT }}>
            <strong>{L.cree}</strong> {L.mailParti}
            <div style={{ marginTop: 12 }}>
              <a href={result.loginUrl || '/'} style={{ color: TEAL, fontWeight: 700 }}>{L.seConnecter}</a>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontSize: 12, color: MUTED }}>{L.societe}
              <input value={nom} onChange={(e) => setNom(e.target.value)} required
                style={inputStyle} placeholder={L.placeholderSociete} />
            </label>
            <label style={{ fontSize: 12, color: MUTED }}>{L.secteur}
              <select value={secteur} onChange={(e) => setSecteur(e.target.value)} style={inputStyle}>
                <option value="">{L.optionnel}</option>
                {secteurs.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12, color: MUTED }}>{L.email}
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                style={inputStyle} placeholder={L.placeholderEmail} />
            </label>
            <label style={{ fontSize: 12, color: MUTED }}>{L.mdp}
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                minLength={8} style={inputStyle} />
            </label>
            {result?.error && (
              <div style={{ color: '#ef4444', fontSize: 13 }}>{result.error}</div>
            )}
            <button type="submit" disabled={loading}
              style={{
                marginTop: 8, padding: '14px 20px', background: TEAL, color: '#052e2b',
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15,
                cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.6 : 1,
              }}>
              {loading ? L.creation : L.demarrer}
            </button>
            <p style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
              {L.note}
            </p>
            <p style={{ fontSize: 13, color: MUTED, textAlign: 'center', marginTop: 4 }}>
              {L.dejaCompte} <a href="/login" style={{ color: TEAL, fontWeight: 700 }}>{L.seConnecterSimple}</a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  display: 'block', width: '100%', marginTop: 4, padding: '10px 12px',
  borderRadius: 8, border: '1px solid rgba(255,255,255,.12)', background: 'rgba(255,255,255,.03)',
  color: TEXT, fontSize: 14, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
};
